import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    // Get token before clearing (like mobile app)
    const token = localStorage.getItem('AUTH_TOKEN');
    
    // Clear localStorage like mobile app
    localStorage.removeItem('AUTH_TOKEN');
    localStorage.removeItem('user_data');
    localStorage.removeItem('is_guest');
    setUser(null);
    console.log("Logout successful");
    setLoading(false);
    
    // Optionally call logout endpoint if token existed
    if (token) {
      fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include",
      }).catch((error) => console.error("Logout API error:", error));
    }
  };

  const checkAuth = useCallback(() => {
    // Check for token in localStorage (like mobile app)
    // Support both 'AUTH_TOKEN' and 'authToken' keys for compatibility
    const token = localStorage.getItem('AUTH_TOKEN') || localStorage.getItem('authToken');
    const userData = localStorage.getItem('user_data');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setUser(user);
        setLoading(false);
        
        // Ensure userId and username are also stored separately (for WebSocket connection)
        if (user.id && !localStorage.getItem('userId')) {
          localStorage.setItem('userId', String(user.id));
        }
        if (user.username && !localStorage.getItem('username')) {
          localStorage.setItem('username', user.username);
        }
        
        return;
      } catch (e) {
        console.error('Error parsing user data:', e);
        // Clear invalid data
        localStorage.removeItem('AUTH_TOKEN');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user_data');
        localStorage.removeItem('userId');
        localStorage.removeItem('username');
      }
    }
    
    // If no token/userData, set loading to false immediately (user not logged in)
    if (!token && !userData) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    // Fallback to API check (for cookie-based auth or to verify token)
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
      headers: { 
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {})
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.ok) {
          return response.json();
        }

        throw new Error("Unauthorized");
      })
      .then((data) => {
        console.log("USER", data);
        // Handle different response formats
        const user = data?.data?.d ?? data?.user ?? data?.data ?? null;
        if (user) {
          setUser(user);
          // Store in localStorage for consistency with mobile app
          localStorage.setItem('user_data', JSON.stringify(user));
          if (token) {
            localStorage.setItem('AUTH_TOKEN', token);
          }
        } else {
          setUser(null);
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error('Auth check error:', error);
        setUser(null);
        // Clear invalid auth data
        localStorage.removeItem('AUTH_TOKEN');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user_data');
        localStorage.removeItem('userId');
        localStorage.removeItem('username');
        localStorage.removeItem('is_guest');
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeUsername = async (newUsername) => {
    if (!user?.id?.length) return; // user not logged in

    try {
      const response = await fetch(
        `${process.env.REACT_APP_MULTIPLAYER_API_URL}/user/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userId: user?.id, username: newUsername }),
        }
      );
      if (response.ok) {
        response.json();

        checkAuth();
      }

      throw new Error("Failed to update username");
    } catch (error) {
      console.error("Update username error:", error);
    }
  };

  const values = useMemo(
    () => ({
      loading,
      user,
      username: user?.username,
      userId: user?.id,
      checkAuth,
      logout,
      onChangeUsername,
    }),
    [checkAuth, loading, user]
  );

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
